import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const PAIN_POINTS = [
  "State licensing documentation requirements changing frequently",
  "Testing documentation gaps that risk license suspension",
  "Lean teams with no dedicated QA manager",
  "SOP consistency issues across shifts or multiple sites",
  "No structured process for nonconformance or CAPA records",
];

export default function CannabisHempLabs() {
  return (
    <>
      <SEO
        title="Cannabis & Hemp Lab Compliance Documentation | LabComplyx"
        description="LabComplyx helps cannabis and hemp testing labs meet state compliance requirements with governed documentation workflows, SOP management, and quality records — built for lean teams."
        canonical="https://labcomplyx.com/industries/cannabis-hemp"
      />
      <div className="pt-24">
        <section className="pb-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="inline-block text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">State Compliance · ISO 17025</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5 leading-tight">
                Quality documentation for cannabis and hemp labs with lean teams
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                LabComplyx helps cannabis and hemp testing labs navigate state compliance requirements with governed documentation workflows — without the overhead of a dedicated QA department.
              </p>
              <Link to="/ValidationIntake">
                <Button size="lg" className="gap-2">
                  Book a Free Compliance Review <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Compliance challenges for cannabis labs</h2>
                <ul className="space-y-3">
                  {PAIN_POINTS.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-7">
                <h3 className="font-semibold text-foreground mb-4">How LabComplyx helps cannabis labs</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  We provide structured quality documentation workflows that keep your lab in compliance with state requirements and ISO 17025 best practices, even without a large QA team.
                </p>
                <ul className="space-y-2.5">
                  {["State-specific SOP templates and management", "Nonconformance and corrective action records", "Testing documentation for licensing audits", "Quality manual and procedure drafting", "Lean workflow design for small QA teams"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ISO 17025 and state cannabis compliance scoring */}
        <section className="py-14 bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-foreground mb-2">How cannabis labs are evaluated on compliance readiness</h2>
            <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
              State cannabis testing lab programs and ISO 17025 accreditation bodies assess labs across overlapping domains. Understanding these scoring areas helps labs prioritize documentation gaps before inspections.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-foreground mb-1">ISO 17025 accreditation areas</h3>
                <p className="text-xs text-muted-foreground mb-4">Assessed by A2LA, Perry Johnson, and similar accreditation bodies</p>
                <ul className="space-y-2.5">
                  {[
                    "Impartiality and confidentiality policies",
                    "Document control and record management",
                    "Personnel competency and training records",
                    "Equipment calibration and maintenance logs",
                    "Method validation and measurement uncertainty",
                    "Proficiency testing participation and results",
                    "Nonconformance, CAPA, and corrective action records",
                    "Internal audit program documentation",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-foreground mb-1">State cannabis agency inspection focus areas</h3>
                <p className="text-xs text-muted-foreground mb-4">Requirements vary by state — examples drawn from CA (BCC/CDPH), CO (CDPHE), MI (MRA), OR (OHA), and others</p>
                <ul className="space-y-2.5">
                  {[
                    "Chain of custody and sample handling documentation",
                    "Current, approved SOPs for all test categories",
                    "QC sample records (blanks, spikes, matrix duplicates)",
                    "Proficiency testing results for mandated analytes",
                    "Personnel training logs and competency assessments",
                    "Corrective action records for out-of-specification results",
                    "Data integrity controls and audit trail requirements",
                    "Scope of accreditation aligned with license authorization",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground mt-4 border-t border-border pt-3">
                  State requirements change frequently. Verify current requirements directly with your state cannabis regulatory agency.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Not a LIMS statement + capabilities + blog links */}
        <section className="py-10 bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-muted/40 border border-border rounded-xl p-5 max-w-2xl mb-8">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">LabComplyx is not a LIMS or full QMS replacement.</strong> It is a governed documentation and QA-support layer that works alongside your existing systems — helping lean cannabis and hemp lab teams meet state compliance requirements without overhauling your operations.
              </p>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-4">Related capabilities</h2>
            <div className="flex flex-wrap gap-3 mb-6">
              {[
                { label: "SOP Management", href: "/solutions/sop-management" },
                { label: "Deviation Documentation", href: "/solutions/deviation-documentation" },
                { label: "Inspection Readiness", href: "/solutions/inspection-readiness" },
                { label: "CAPA Documentation", href: "/solutions/capa-documentation" },
              ].map((c) => (
                <Link key={c.href} to={c.href} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-border bg-card hover:border-primary/40 hover:text-primary transition-colors">
                  {c.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
            <h3 className="text-base font-semibold text-foreground mb-3">Relevant guides & resources</h3>
            <div className="flex flex-wrap gap-3">
              {[
                { label: "Lab SOP Management", href: "/blog/lab-sop-management" },
                { label: "CAPA Root Cause Analysis", href: "/blog/capa-root-cause-analysis" },
              ].map((b) => (
                <Link key={b.href} to={b.href} className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-border bg-muted/30 hover:border-primary/40 hover:text-primary transition-colors">
                  {b.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Reduce your state compliance documentation risk</h2>
            <p className="text-primary-foreground/80 mb-8">Book a free compliance review to identify gaps in your lab's quality documentation before your next state inspection or licensing audit.</p>
            <Link to="/ValidationIntake">
              <Button size="lg" variant="secondary" className="gap-2">
                Book a Free Compliance Review <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
